import { supabase } from '@/integrations/supabase/client';
import {
  ContratoControleQualidade,
  ContratoControleQualidadeStatus,
  CreateContratoControleQualidadeDto,
  UpdateContratoControleQualidadeDto,
  IniciarControleQualidadeDto,
  FinalizarControleQualidadeDto,
  ContratoControleQualidadeFilters
} from '@/features/contratos/types/contrato-controle-qualidade.types';

// ── Selector base reutilizado em todas as queries ─────────────────────────────
// Usa hints de FK explícitos para evitar PGRST201 — contratos tem dois FKs
// para consultores: contratos_consultor_id_fkey e contratos_relacionador_id_fkey
const SELECT_FIELDS = `
  *,
  contratos(
    id, contrato_numero, cliente_id, consultor_id, relacionador_id,
    tenant_id, status, data_contrato, data_vencimento, created_at, updated_at,
    cliente:clientes(id, nome, cpf),
    consultor:consultores!contratos_consultor_id_fkey(id, nome)
  ),
  responsavel:consultores(id, nome, email)
`;

// ── Mapper ────────────────────────────────────────────────────────────────────
const mapearControleQualidade = (raw: any): ContratoControleQualidade => ({
  id:                   raw.id,
  contrato_id:          raw.contrato_id,
  tenant_id:            raw.tenant_id,
  responsavel_id:       raw.responsavel_id,
  // Banco usa 'status_controle', o tipo usa 'status'
  status:               raw.status_controle as ContratoControleQualidadeStatus,
  data_inicio_analise:  raw.data_inicio_analise ?? undefined,
  data_fim_analise:     raw.data_fim_analise ?? undefined,
  observacoes:          raw.observacoes ?? undefined,
  motivo_reprovacao:    raw.motivo_reprovacao ?? undefined,
  checklist:            raw.checklist ?? undefined,
  // Retornar como string (ISO) — conforme tipo ContratoControleQualidade
  created_at:           raw.created_at,
  updated_at:           raw.updated_at ?? raw.created_at,
  contrato: raw.contratos ? {
    id:               raw.contratos.id,
    contrato_numero:  raw.contratos.contrato_numero,
    cliente:          raw.contratos.cliente   ? { id: raw.contratos.cliente.id,   nome: raw.contratos.cliente.nome,   cpf: raw.contratos.cliente.cpf }   : undefined,
    consultor:        raw.contratos.consultor ? { id: raw.contratos.consultor.id, nome: raw.contratos.consultor.nome } : undefined,
  } : undefined,
  responsavel: raw.responsavel ? {
    id:    raw.responsavel.id,
    nome:  raw.responsavel.nome,
    email: raw.responsavel.email,
  } : undefined,
});

// ── Serviço ───────────────────────────────────────────────────────────────────
export class ContratoControleQualidadeSupabaseService {

  /** Cria um novo registro de controle de qualidade */
  async create(data: CreateContratoControleQualidadeDto): Promise<ContratoControleQualidade> {
    const { data: result, error } = await supabase
      .from('contrato_controle_qualidade')
      .insert({
        contrato_id:     data.contrato_id,
        tenant_id:       data.tenant_id ?? 1, // TODO: pegar do contexto do tenant logado
        status_controle: 'pendente',
        observacoes:     data.observacoes,
        checklist:       data.checklist ?? {},
      })
      .select(SELECT_FIELDS)
      .single();

    if (error) {
      console.error('Erro ao criar controle de qualidade:', error);
      throw error;
    }
    return mapearControleQualidade(result);
  }

  /** Lista todos os controles de qualidade */
  async findAll(): Promise<ContratoControleQualidade[]> {
    const { data, error } = await supabase
      .from('contrato_controle_qualidade')
      .select(SELECT_FIELDS)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao listar controles de qualidade:', error);
      throw error;
    }
    return (data ?? []).map(mapearControleQualidade);
  }

  /** Busca um controle de qualidade por ID */
  async findOne(id: number): Promise<ContratoControleQualidade> {
    const { data, error } = await supabase
      .from('contrato_controle_qualidade')
      .select(SELECT_FIELDS)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Erro ao buscar controle de qualidade:', error);
      throw error;
    }
    return mapearControleQualidade(data);
  }

  /** Busca o controle de qualidade de um contrato específico */
  async findByContrato(contratoId: number): Promise<ContratoControleQualidade | null> {
    const { data, error } = await supabase
      .from('contrato_controle_qualidade')
      .select(SELECT_FIELDS)
      .eq('contrato_id', contratoId)
      .maybeSingle(); // maybeSingle → retorna null sem lançar erro se não existir

    if (error) {
      console.error('Erro ao buscar controle de qualidade por contrato:', error);
      throw error;
    }
    return data ? mapearControleQualidade(data) : null;
  }

  /** Atualiza campos livres de um controle de qualidade */
  async update(id: number, data: UpdateContratoControleQualidadeDto): Promise<ContratoControleQualidade> {
    const payload: Record<string, unknown> = {};
    if (data.status          !== undefined) payload.status_controle      = data.status;
    if (data.observacoes     !== undefined) payload.observacoes          = data.observacoes;
    if (data.motivo_reprovacao !== undefined) payload.motivo_reprovacao  = data.motivo_reprovacao;
    if (data.checklist       !== undefined) payload.checklist            = data.checklist;
    if (data.data_inicio_analise !== undefined) payload.data_inicio_analise = data.data_inicio_analise;
    if (data.data_fim_analise    !== undefined) payload.data_fim_analise    = data.data_fim_analise;

    const { data: result, error } = await supabase
      .from('contrato_controle_qualidade')
      .update(payload)
      .eq('id', id)
      .select(SELECT_FIELDS)
      .single();

    if (error) {
      console.error('Erro ao atualizar controle de qualidade:', error);
      throw error;
    }
    return mapearControleQualidade(result);
  }

  /**
   * Inicia a análise: muda status → em_analise, registra responsável e data
   * Também atualiza contratos.status → 'em_andamento' (regra de negócio)
   */
  async iniciarControleQualidade(id: number, data: IniciarControleQualidadeDto): Promise<ContratoControleQualidade> {
    const { data: result, error } = await supabase
      .from('contrato_controle_qualidade')
      .update({
        status_controle:     'em_analise',
        responsavel_id:      data.responsavel_id,
        data_inicio_analise: new Date().toISOString(),
        observacoes:         data.observacoes,
      })
      .eq('id', id)
      .select(SELECT_FIELDS)
      .single();

    if (error) {
      console.error('Erro ao iniciar controle de qualidade:', error);
      throw error;
    }

    // Cascade: atualiza o status global do contrato para 'em_andamento'
    if (result?.contrato_id) {
      const { error: contratoError } = await supabase
        .from('contratos')
        .update({ status: 'em_andamento' })
        .eq('id', result.contrato_id);

      if (contratoError) {
        console.warn('Aviso: não foi possível atualizar status do contrato para em_andamento:', contratoError);
      }
    }

    return mapearControleQualidade(result);
  }

  /**
   * Finaliza a análise com o resultado (aprovado/reprovado/finalizado)
   * Se APROVADO: atualiza contratos.status → 'ativo' (regra de negócio central)
   */
  async finalizarControleQualidade(id: number, data: FinalizarControleQualidadeDto): Promise<ContratoControleQualidade> {
    if (data.status === ContratoControleQualidadeStatus.REPROVADO && !data.motivo_reprovacao?.trim()) {
      throw new Error('Motivo da reprovação é obrigatório ao reprovar uma auditoria.');
    }

    const { data: result, error } = await supabase
      .from('contrato_controle_qualidade')
      .update({
        status_controle:   data.status,
        observacoes:       data.observacoes,
        motivo_reprovacao: data.motivo_reprovacao,
        checklist:         data.checklist,
        data_fim_analise:  new Date().toISOString(),
      })
      .eq('id', id)
      .select(SELECT_FIELDS)
      .single();

    if (error) {
      console.error('Erro ao finalizar controle de qualidade:', error);
      throw error;
    }

    // Cascade de status no contrato conforme regra de negócio:
    // APROVADO → contrato fica 'ativo' | REPROVADO → contrato volta para 'assinado'
    if (result?.contrato_id) {
      const novoStatusContrato =
        data.status === ContratoControleQualidadeStatus.APROVADO ? 'ativo' : 'assinado';

      const { error: contratoError } = await supabase
        .from('contratos')
        .update({ status: novoStatusContrato })
        .eq('id', result.contrato_id);

      if (contratoError) {
        console.warn(`Aviso: não foi possível atualizar status do contrato para ${novoStatusContrato}:`, contratoError);
      }
    }

    return mapearControleQualidade(result);
  }

  /** Remove um controle de qualidade */
  async remove(id: number): Promise<void> {
    const { error } = await supabase
      .from('contrato_controle_qualidade')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao remover controle de qualidade:', error);
      throw error;
    }
  }

  /** Lista controles com status PENDENTE */
  async getChecagensPendentes(): Promise<ContratoControleQualidade[]> {
    const { data, error } = await supabase
      .from('contrato_controle_qualidade')
      .select(SELECT_FIELDS)
      .eq('status_controle', 'pendente')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapearControleQualidade);
  }

  /** Lista controles com status EM_ANALISE */
  async getChecagensEmAnalise(): Promise<ContratoControleQualidade[]> {
    const { data, error } = await supabase
      .from('contrato_controle_qualidade')
      .select(SELECT_FIELDS)
      .eq('status_controle', 'em_analise')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapearControleQualidade);
  }

  /** Lista controles finalizados (aprovado ou reprovado) */
  async getChecagensFinalizadas(): Promise<ContratoControleQualidade[]> {
    const { data, error } = await supabase
      .from('contrato_controle_qualidade')
      .select(SELECT_FIELDS)
      .in('status_controle', ['aprovado', 'reprovado', 'finalizado'])
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapearControleQualidade);
  }

  /** Lista controles com filtros dinâmicos */
  async getChecagensByFilters(filters: ContratoControleQualidadeFilters): Promise<ContratoControleQualidade[]> {
    let query = supabase
      .from('contrato_controle_qualidade')
      .select(SELECT_FIELDS);

    if (filters.status)         query = query.eq('status_controle', filters.status);
    if (filters.responsavel_id) query = query.eq('responsavel_id', filters.responsavel_id);
    if (filters.contrato_id)    query = query.eq('contrato_id', filters.contrato_id);
    if (filters.data_inicio)    query = query.gte('created_at', filters.data_inicio);
    if (filters.data_fim)       query = query.lte('created_at', filters.data_fim);

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapearControleQualidade);
  }
}
